/** Ephemeral source mapping: normalization must never manufacture provenance. */
export type ReminderSourceSpan = Readonly<{ start: number; end: number; text: string }>;

export function hasUnsupportedReminderControl(value: string): boolean {
  return Array.from(value).some((character) => {
    const code = character.charCodeAt(0);
    return (code < 32 && code !== 9 && code !== 10 && code !== 13) || code === 127;
  });
}
export type NormalizedReminderText = Readonly<{
  original: string;
  text: string;
  spans: ReadonlyArray<Readonly<{ start: number; end: number }>>;
}>;

function replace(
  input: NormalizedReminderText,
  pattern: RegExp,
  transform: (match: RegExpExecArray) => string,
): NormalizedReminderText {
  let text = "",
    cursor = 0;
  const spans: Array<{ start: number; end: number }> = [];
  for (const match of input.text.matchAll(pattern)) {
    const start = match.index,
      end = start + match[0].length;
    text += input.text.slice(cursor, start);
    spans.push(...input.spans.slice(cursor, start));
    const next = transform(match);
    text += next;
    const source = {
      start: input.spans[start]?.start ?? 0,
      end: input.spans[end - 1]?.end ?? input.original.length,
    };
    for (let index = 0; index < next.length; index++) spans.push(source);
    cursor = end;
  }
  text += input.text.slice(cursor);
  spans.push(...input.spans.slice(cursor));
  return { original: input.original, text, spans };
}

export function reminderOriginalSpan(
  input: NormalizedReminderText,
  start: number,
  end: number,
): ReminderSourceSpan {
  const sourceStart = input.spans[start]?.start ?? input.original.length;
  const sourceEnd = end > start ? (input.spans[end - 1]?.end ?? sourceStart) : sourceStart;
  return { start: sourceStart, end: sourceEnd, text: input.original.slice(sourceStart, sourceEnd) };
}

const numbers: Readonly<Record<string, number>> = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
};
const units = "(?:one|two|three|four|five|six|seven|eight|nine)";
const tens = "(?:twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)";
const small = `(?:${tens}(?:[ -]+${units})?|zero|${units}|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen)`;
// “one and two days” is a coordinated list, whereas “one hundred and two” is one number.
const numberPhrase = new RegExp(
  `\\b(?:${units}[ -]+hundred(?:[ -]+(?:and[ -]+)?${small})?|hundred|${small})\\b`,
  "giu",
);

export function normalizeReminderEnglish(original: string): NormalizedReminderText {
  let value: NormalizedReminderText = {
    original,
    text: original,
    spans: Array.from({ length: original.length }, (_, index) => ({
      start: index,
      end: index + 1,
    })),
  };
  value = replace(value, numberPhrase, (match) => {
    let total = 0;
    for (const word of match[0].toLowerCase().split(/[ -]+/u)) {
      if (word === "and") continue;
      total = word === "hundred" ? Math.max(1, total) * 100 : total + (numbers[word] ?? 0);
    }
    return String(total);
  });
  const ordinals: Readonly<Record<string, string>> = {
    first: "1",
    second: "2",
    third: "3",
    fourth: "4",
    fifth: "5",
    sixth: "6",
    seventh: "7",
    eighth: "8",
    ninth: "9",
    tenth: "10",
    eleventh: "11",
    twelfth: "12",
  };
  value = replace(
    value,
    /\b(first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth)\s+of\s+(January|February|March|April|May|June|July|August|September|October|November|December)\b/giu,
    (match) => `${ordinals[match[1]!.toLowerCase()]} ${match[2]}`,
  );
  value = replace(
    value,
    /\b(\d{1,2})\s+in\s+the\s+(morning|afternoon|evening)\b/giu,
    (match) => `${match[1]} ${match[2]!.toLowerCase() === "morning" ? "am" : "pm"}`,
  );
  value = replace(
    value,
    /\bhalf past (\d{1,2})(\s*(?:am|pm))?\b/giu,
    (match) => `${match[1]}:30${match[2] ?? ""}`,
  );
  value = replace(
    value,
    /\bquarter past (\d{1,2})(\s*(?:am|pm))?\b/giu,
    (match) => `${match[1]}:15${match[2] ?? ""}`,
  );
  value = replace(value, /\bquarter to noon\b/giu, () => "11:45 am");
  value = replace(value, /\b(?:(?:12\s+)?noon|mid[ -]?day)\b/giu, () => "12:00 pm");
  value = replace(value, /\bmidnight\b/giu, () => "12:00 am");
  value = replace(value, /\b([ap])\.\s*m\.?/giu, (match) => `${match[1]}m`);
  value = replace(value, /\b(a\s+)?fortnight\b/giu, () => "2 weeks");
  value = replace(value, /\ba\s+week\b/giu, () => "1 week");
  value = replace(
    value,
    /\b(?:the day before|the previous day|previous day)\b/giu,
    () => "1 day before",
  );
  value = replace(value, /\bon the day\b/giu, () => "0 days before");
  return value;
}
